import React, { Fragment, useEffect } from 'react';

import { Box, Divider, Grid, Typography } from '@mui/material';
import { FormikValues, useFormik } from 'formik';

import {
  IAdminConfiguration,
  adminConfiguration,
  adminGetPharmacyAdminProfile,
  adminPharmacyAdminProfile,
  editAdminConfiguration,
} from '@pharmaplan/common';

import CustomButton from '../../../common/CustomButton';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { formGenerator } from '../../../../helpers/formGenerator';
import useSelfService from '../../../../hooks/useSelfService';
import strings from '../../../../localization';

import { adminConfigurationValidator } from './validator';
import { adminEditFormConfig } from './helper';
import styles from './style';
import useAdminPermissions from '../../../../hooks/Admin/useAdminPermissions';

const EditAdminConfiguration = () => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();
  const { can } = useAdminPermissions();

  const adminConfig = useAppSelector(adminConfiguration);
  const adminParams = useAppSelector(adminPharmacyAdminProfile);
  const { provinces } = adminParams ?? {};

  const {
    tpsPercentage: adminTps,
    tvqPercentage: adminTvq,
    bookingInvitationConfiguration,
    sendBookingInvitationReminder,
  } = adminConfig ?? {};

  const onSubmit = (values: FormikValues) => {
    const {
      minHourWorkshift: formikMinHoursWorkshift,
      pharmacyRate: formikPharmacyRate,
      pharmacistRate: formikPharmacistRate,
      ratePharmacistDays: formikRatePharmacistDays,
      technicianRate: formikTechnicianRate,
      bookingStateExpiration: formikBookingState,
      pharmacyReminderBefore,
      enableChat,
      tpsPercentage,
      tvqPercentage,
      taxOnTax,
      professionalCapacity,
      softwareKnowledge,
      preferredShiftHours,
      remoteRegions,
      maxTravelDistance,
      includePPShifts,
      availability,
      repeatAfterDays,
    } = values ?? {};

    const formikBookingInvitationConfiguration = {
      professionalCapacity,
      softwareKnowledge,
      preferredShiftHours,
      remoteRegions,
      maxTravelDistance,
      includePPShifts,
      availability,
      repeatAfterDays,
    };

    const modifiedValues = {
      ...values,
      bookingInvitationConfiguration: formikBookingInvitationConfiguration,
      ratePharmacistDays: parseInt(formikRatePharmacistDays, 10),
      minHourWorkshift: parseFloat(formikMinHoursWorkshift),
      pharmacyRate: parseFloat(formikPharmacyRate),
      pharmacistRate: parseFloat(formikPharmacistRate),
      technicianRate: parseFloat(formikTechnicianRate),
      enableChat,
    };

    const selfServiceValues = {
      ...modifiedValues,
      bookingStateExpiration: parseFloat(formikBookingState),
      pharmacyReminderBefore: parseFloat(pharmacyReminderBefore),
      tpsPercentage: parseFloat(tpsPercentage),
      tvqPercentage: parseFloat(tvqPercentage),
      taxOnTax,
    };

    dispatch(
      editAdminConfiguration(
        isSelfService
          ? { ...(selfServiceValues as IAdminConfiguration) }
          : { ...(modifiedValues as IAdminConfiguration) },
      ),
    );
  };

  const formik = useFormik({
    initialValues: {
      ...adminConfig,
      ...bookingInvitationConfiguration,
      sendBookingInvitationReminder,
      tpsPercentage: adminTps || null,
      tvqPercentage: adminTvq || null,
      syncTechnicianShifts: adminConfig?.syncTechnicianShifts ?? true,
      syncPharmacistShifts: adminConfig?.syncPharmacistShifts ?? true,
    },
    enableReinitialize: true,
    onSubmit,
    validationSchema: adminConfigurationValidator,
  });

  const { handleSubmit, errors } = formik ?? {};
  console.log(errors);

  useEffect(() => {
    dispatch(adminGetPharmacyAdminProfile());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={styles.root}>
      {adminEditFormConfig(
        provinces,
        !formik.values?.sendBookingInvitationReminder,
        can,
      ).map((item) => {
        const { key, title, form } = item ?? {};

        return (
          <Fragment key={key}>
            <Box sx={styles.container}>
              <Typography sx={styles.titleFont}>{title}</Typography>
              <Divider sx={styles.divider} />
            </Box>

            <Grid container xs={12}>
              {form?.().map((formItem) =>
                formGenerator(formItem, formik))}
            </Grid>
          </Fragment>
        );
      })}
      <CustomButton
        customButtonStyle={styles.button}
        customClass={styles.buttonContainer}
        label={strings.save}
        onClick={handleSubmit}
      />
    </Box>
  );
};

export default EditAdminConfiguration;
